import React, { useState, useEffect } from 'react';
import { Apple, Loader2 } from 'lucide-react';
import { initializeApplePay, processApplePayment } from '../../lib/payments';

interface ApplePayButtonProps {
  amount: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  disabled?: boolean;
}

export function ApplePayButton({ amount, onSuccess, onError, disabled }: ApplePayButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    checkApplePayAvailability();
  }, []);

  const checkApplePayAvailability = async () => {
    try {
      // Don't show on mobile devices
      if (isMobile) {
        return;
      }

      // Check if Apple Pay is supported by the browser
      if (!window.ApplePaySession) {
        console.log('Apple Pay is not supported by the browser');
        return;
      }

      // Check if Apple Pay is available
      if (!ApplePaySession.canMakePayments()) {
        console.log('Apple Pay is not available');
        return;
      }

      // Check if user has an active card
      const canMakePayments = await ApplePaySession.canMakePaymentsWithActiveCard('merchant.07am.app');
      console.log('Can make payments with active card:', canMakePayments);
      setIsApplePayAvailable(canMakePayments);
    } catch (err) {
      console.error('Error checking Apple Pay availability:', err);
      setIsApplePayAvailable(false);
    }
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Initializing Apple Pay...');
      
      // Initialize Apple Pay session
      const session = await initializeApplePay(amount);

      if (!session) {
        throw new Error('Apple Pay is not available');
      }

      console.log('Processing Apple Pay payment...');
      await processApplePayment(
        session,
        () => {
          console.log('Payment successful');
          onSuccess();
          setLoading(false);
        },
        (error) => {
          console.error('Payment error:', error);
          setError(error);
          onError(error);
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('Apple Pay error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Payment failed to initialize';
      setError(errorMessage);
      onError(errorMessage);
      setLoading(false);
    }
  };

  // Don't render the button if Apple Pay is not available
  if (!isApplePayAvailable) {
    return null;
  }

  return (
    <>
      <button
        onClick={handlePayment}
        disabled={disabled || loading}
        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-black text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Apple className="w-5 h-5" />
        )}
        <span>{loading ? 'Processing...' : 'Pay with Apple Pay'}</span>
      </button>
      {error && (
        <div className="mt-2 text-sm text-red-500 text-center">
          {error}
        </div>
      )}
    </>
  );
}